// ─── AlignmentToolbar (Phase 1.8) ─────────────────────────────
// Compact icon row shown when a single element is selected.
// Mounted in the editor sidebar above ElementStylePanel.
//
// Actions: align L/R/T/B, center H, center V, bring forward, send back,
// duplicate, lock toggle. Plus a role switcher for image elements.
// Distribute / equal-spacing is intentionally NOT here (Phase 1.9).

import React from 'react';
import {
  AlignLeft, AlignRight, AlignVerticalJustifyStart, AlignVerticalJustifyEnd,
  AlignCenterHorizontal, AlignCenterVertical, Copy, ArrowUp, ArrowDown,
  Lock, Unlock, ImageIcon, Scan,
} from 'lucide-react';
import { alignElement, bringForward, sendBackward, duplicateElement } from '../../lib/utils/alignmentActions';
import { applyImageRole, IMAGE_ROLE_OPTIONS, IMAGE_ROLES, lockAndFitCard } from '../../lib/utils/imageRoles';

const ACTIONS = [
  { code: 'align-left',         Icon: AlignLeft,                title: 'Align left' },
  { code: 'center-horizontal',  Icon: AlignCenterHorizontal,    title: 'Center horizontally' },
  { code: 'align-right',        Icon: AlignRight,               title: 'Align right' },
  { code: 'align-top',          Icon: AlignVerticalJustifyStart,title: 'Align top' },
  { code: 'center-vertical',    Icon: AlignCenterVertical,      title: 'Center vertically' },
  { code: 'align-bottom',       Icon: AlignVerticalJustifyEnd,  title: 'Align bottom' },
];

export default function AlignmentToolbar({
  element,
  sectionId,
  allElements,
  onUpdate,
  onAdd,
  onToggleLock,
}) {
  if (!element || !sectionId) return null;
  const locked = !!element.locked;
  const isImage = element.type === 'image_card';
  const currentRole = element.role || IMAGE_ROLES.IMAGE_LAYER;

  const applyAlign = (code) => {
    if (locked) return;
    const next = alignElement(element, code);
    const mobile = next.layout?.mobile || {};
    // Send a layout override patch matching the editor's data shape.
    onUpdate(sectionId, element.id, {
      layout: {
        ...(element.layout || {}),
        mobile: { ...(element.layout?.mobile || {}), ...mobile },
      },
    });
  };

  const applyZ = (dir) => {
    if (locked) return;
    const next = dir === 'forward'
      ? bringForward(element, allElements || [])
      : sendBackward(element);
    const z = next.layout?.mobile?.z;
    if (typeof z === 'number') {
      onUpdate(sectionId, element.id, {
        layout: {
          ...(element.layout || {}),
          mobile: { ...(element.layout?.mobile || {}), z },
          desktop: { ...(element.layout?.desktop || {}), z },
        },
      });
    }
  };

  const handleDuplicate = () => {
    if (!onAdd) return;
    const copy = duplicateElement(element);
    if (copy) onAdd(sectionId, copy);
  };

  const handleRoleChange = (role) => {
    if (locked || role === currentRole) return;
    // Preserve existing position when switching role — only style/anchor/z shift.
    const next = applyImageRole(element, role, { preserveExistingPosition: true });
    onUpdate(sectionId, element.id, {
      role,
      customStyle: next.customStyle,
      locked: next.locked,
      layout: next.layout,
    });
  };

  // Phase 1.8 polish — "Lock & fit" one-tap for Card layer.
  const handleLockAndFit = () => {
    const next = lockAndFitCard(element);
    onUpdate(sectionId, element.id, {
      role: next.role,
      locked: next.locked,
      customStyle: next.customStyle,
      layout: next.layout,
    });
  };

  return (
    <div
      className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-2 space-y-2"
      data-testid="alignment-toolbar"
    >
      {/* Alignment row */}
      <div className="flex items-center gap-0.5">
        {ACTIONS.map(({ code, Icon, title }) => (
          <button
            key={code}
            onClick={() => applyAlign(code)}
            disabled={locked}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
            title={title}
            data-testid={`align-${code}`}
          >
            <Icon className="w-3.5 h-3.5" />
          </button>
        ))}
        <span className="mx-1 w-px h-4 bg-gray-200 dark:bg-gray-800" />
        <button
          onClick={() => applyZ('forward')}
          disabled={locked}
          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
          title="Bring forward"
          data-testid="bring-forward-btn"
        >
          <ArrowUp className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => applyZ('back')}
          disabled={locked}
          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
          title="Send backward"
          data-testid="send-backward-btn"
        >
          <ArrowDown className="w-3.5 h-3.5" />
        </button>
        <span className="mx-1 w-px h-4 bg-gray-200 dark:bg-gray-800" />
        <button
          onClick={handleDuplicate}
          disabled={locked}
          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
          title="Duplicate"
          data-testid="duplicate-btn"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => onToggleLock?.(sectionId, element.id, !locked)}
          className={`p-1.5 rounded transition-colors ${
            locked
              ? 'text-amber-500 bg-amber-50 dark:bg-amber-900/20'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
          }`}
          title={locked ? 'Unlock' : 'Lock'}
          data-testid="alignment-lock-btn"
        >
          {locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* Image role switcher */}
      {isImage && (
        <div className="pt-2 border-t border-gray-100 dark:border-gray-800 space-y-2">
          <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide flex items-center gap-1">
            <ImageIcon className="w-3 h-3" /> Image role
          </label>
          <select
            value={currentRole}
            onChange={(e) => handleRoleChange(e.target.value)}
            disabled={locked}
            className="w-full text-xs px-2 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 disabled:opacity-50"
            data-testid="image-role-switcher"
          >
            {IMAGE_ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {/* Phase 1.8 polish — Lock & fit (Card layer only) */}
          {currentRole === IMAGE_ROLES.PAPER_CARD && (
            <button
              onClick={handleLockAndFit}
              className="w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-[11px] font-medium bg-emerald-600 text-white hover:bg-emerald-700"
              title="Center this card, fit it to mobile width, and lock it."
              data-testid="card-lock-and-fit-btn"
            >
              <Scan className="w-3 h-3" />
              Lock &amp; fit
            </button>
          )}
        </div>
      )}
    </div>
  );
}
